import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowLeft, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { StartupComments } from '@/components/startup/StartupComments';
import { StartupInfoCard } from '@/components/startup/StartupInfoCard';
import { StartupRoles } from '@/components/startup/StartupRoles';
import { StartupDescription } from '@/components/startup/StartupDescription';
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { LoginPromptDialog } from "@/components/startup/LoginPromptDialog";
import { StartupHeader } from "@/components/startup/details/StartupHeader";
import { StartupBanner } from "@/components/startup/details/StartupBanner";

const StartupDetails = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [showLoginPrompt, setShowLoginPrompt] = useState(!user);

  const { data: startup, isLoading, refetch } = useQuery({
    queryKey: ['startup', id],
    queryFn: async () => {
      const { data: startup, error } = await supabase
        .from('startups')
        .select(`
          *,
          startup_roles (*),
          profiles (
            id,
            username,
            full_name,
            is_pro
          )
        `)
        .eq('id', id)
        .single();
      
      if (error) throw error;
      setEditedName(startup.name);
      return startup;
    }
  });

  const handleSaveName = async () => {
    if (!startup || !user) return;

    try {
      const { error } = await supabase
        .from('startups')
        .update({ name: editedName })
        .eq('id', startup.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Startup name updated successfully",
      });
      
      setIsEditingName(false);
      refetch();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update startup name",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4 animate-pulse">
        <div className="h-64 bg-gray-700/20 rounded-lg mb-8" />
        <div className="space-y-4">
          <div className="h-8 bg-gray-700/20 rounded w-1/4" />
          <div className="h-24 bg-gray-700/20 rounded" />
        </div>
      </div>
    );
  }

  if (!startup) {
    return (
      <div className="container mx-auto py-8 px-4">
        <h1 className="text-2xl font-bold text-red-500">Startup not found</h1>
      </div>
    );
  }

  const isOwner = user?.id === startup.user_id;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="container mx-auto py-8 px-4 space-y-8"
    >
      <div className="flex items-center justify-between mb-6">
        <Button 
          variant="ghost" 
          className="text-flux-cyan hover:text-flux-cyan/80"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        {isOwner && (
          <Button
            variant="outline"
            className="border-flux-cyan text-flux-cyan hover:bg-flux-cyan/10"
            onClick={() => navigate(`/communities/startups/${startup.id}/edit`)}
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Startup
          </Button>
        )}
      </div>

      {!user && (
        <LoginPromptDialog 
          isOpen={showLoginPrompt}
          onClose={() => setShowLoginPrompt(false)}
        />
      )}

      <StartupBanner bannerUrl={startup.banner_url} name={startup.name} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <StartupHeader
            name={startup.name}
            editedName={editedName}
            isEditingName={isEditingName}
            isPro={startup.profiles?.is_pro}
            category={startup.category}
            stage={startup.stage}
            isOwner={isOwner}
            setEditedName={setEditedName}
            setIsEditingName={setIsEditingName}
            handleSaveName={handleSaveName}
            startup={startup}
          />

          <StartupDescription
            startupId={startup.id}
            shortDescription={startup.short_description}
            description={startup.description}
            isOwner={isOwner}
            onUpdate={refetch}
          />

          <StartupRoles roles={startup.startup_roles} startupId={startup.id} />
          <StartupComments startupId={startup.id} />
        </div>

        <div className="space-y-8">
          <StartupInfoCard 
            teamSize={startup.team_size} 
            websiteUrl={startup.website_url}
            location={startup.location}
            founder={startup.profiles}
            startupId={startup.id}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default StartupDetails;
